'use client'

import { chainValidations, makeFormStateWithModel } from 'formtastisch'
import { type FC, useCallback, useContext, useMemo, useState } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { IconArrowRight } from '@redteclab/icons'

import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  LoadingButton,
  Notification,
} from 'base-ui'

import { validateEmail, validateRequired } from '../../../base-form-handling'
import { FriendlyCaptchaChallenge } from '../../../friendly-captcha'
import { useOneTimePasswordApiRequestMutation } from '../../api/swr/useOneTimePasswordApiRequestMutation'
import {
  LOGIN_OTP_STATE,
  LoginRegistrationUserLoginStateContext,
} from '../../context/LoginRegistrationUserLoginStateContext'

const makeLoginRegistrationOneTimePasswordRequestFormState = (
  intl: IntlShape,
  email: string,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- the rule was disabled before a comment became required
) => {
  return makeFormStateWithModel({
    initialData: {
      fcSolution: '',
      username: email,
    },
    validations: {
      username: (value: string) => {
        return chainValidations(
          () =>
            validateRequired(
              value,
              intl.formatMessage({ id: 'form.error.input.missing' }),
            ),
          () =>
            validateEmail(
              value,
              intl.formatMessage({ id: 'error.email.invalid' }),
            ),
        )
      },
    },
  })
}

type LoginRegistrationOneTimePasswordRequestFormState = ReturnType<
  typeof makeLoginRegistrationOneTimePasswordRequestFormState
>

const handleSubmit = async (
  otpSwr: ReturnType<typeof useOneTimePasswordApiRequestMutation>,
  formState: LoginRegistrationOneTimePasswordRequestFormState,
): Promise<void> => {
  if (otpSwr.isMutating) {
    return
  }

  formState.validate()
  if (!formState.isValid()) {
    return
  }

  const { model } = formState

  try {
    await otpSwr.trigger({
      fcSolution: model.fcSolution,
      username: model.username,
    })
  } catch {
    // void
  }
}

export const LoginRegistrationOneTimePasswordRequestForm: FC<{
  isReRequest?: boolean
  // eslint-disable-next-line max-lines-per-function -- the rule was disabled before a comment became required
}> = ({ isReRequest }) => {
  const intl = useIntl()

  const { setLoginOtpState, setUsername, username } = useContext(
    LoginRegistrationUserLoginStateContext,
  )

  const [isFriendlyCaptchaDone, setIsFriendlyCaptchaDone] =
    useState<boolean>(false)

  const formState = useMemo(
    () => makeLoginRegistrationOneTimePasswordRequestFormState(intl, username),
    [intl, username],
  ).use()

  const { model } = formState

  const captchaDoneCallback = useCallback(
    (fcSolution: string): void => {
      formState.setValue(fcSolution, model, 'fcSolution')
      setIsFriendlyCaptchaDone(true)
    },
    [formState, model],
  )

  const [showGeneralErrorNotification, setShowGeneralErrorNotification] =
    useState(false)

  const otpSwr = useOneTimePasswordApiRequestMutation({
    onError: () => {
      formState.setValue('', model, 'fcSolution')
      setShowGeneralErrorNotification(true)
    },
    onSuccess: () => {
      setUsername(model.username)
      setLoginOtpState(LOGIN_OTP_STATE.OTP_INPUT)
      setShowGeneralErrorNotification(false)
    },
  })

  return (
    <>
      <h2 className="mb-2 text-l font-medium">
        {isReRequest ? (
          <FormattedMessage id="login.otp.reRequestCode" />
        ) : (
          <FormattedMessage id="login.otp.withCode" />
        )}
      </h2>
      {showGeneralErrorNotification ? (
        <Notification severity="error">
          {intl.formatMessage({ id: 'general.error' })}
        </Notification>
      ) : null}
      <p className="mb-4 text-m font-normal">
        <FormattedMessage id="login.otp.willSendEmail" />
      </p>

      {isReRequest ? (
        // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
        <p className="mt-8 font-[var(--font-weight-medium)] text-[var(--font-size-default)]">
          <FormattedMessage id="login.top.pleaseVerifyEmail" />
        </p>
      ) : null}
      <form
        data-clientside-hook="formValidation"
        method="POST"
        name="otp-request-form"
        onSubmit={(event) => {
          event.preventDefault()
          setShowGeneralErrorNotification(false)
          void handleSubmit(otpSwr, formState)
        }}
      >
        <ul className="list-none">
          <li className="mb-6">
            <FormControl
              className="mb-3.5"
              error={!model.validator.isPropertyValid('username')}
              onChange={(event): void => {
                formState.makeInputProps('username').onChange(event)
              }}
              value={model.username || ''}
            >
              <FormLabel htmlFor="form-email">
                {intl.formatMessage({ id: 'general.label.email' })}
              </FormLabel>
              <Input
                id="form-email"
                name="username"
                slotProps={{
                  input: { 'data-qa-id': 'otp-request-email-input' },
                }}
              />
              {model.validator.getFirstErrorFor('username') && (
                <FormHelperText>
                  {model.validator.getFirstErrorFor('username')}
                </FormHelperText>
              )}
            </FormControl>
          </li>
        </ul>
        <LoadingButton
          className="mb-5 w-full"
          data-qa-id="otp-request-submit-button"
          disabled={!isFriendlyCaptchaDone || otpSwr.isMutating}
          id="otp-request-submit-btn"
          loading={otpSwr.isMutating}
          name="otp-request-submit-button"
          type="submit"
        >
          <div className="flex items-center">
            <p className="text-m font-normal">
              {isReRequest ? (
                <FormattedMessage id="login.otp.reRequestCode" />
              ) : (
                <FormattedMessage id="login.otp.requestCode" />
              )}
            </p>
            <IconArrowRight className="size-5" />
          </div>
        </LoadingButton>
        <p className="mb-5 text-s font-normal">
          <FormattedMessage id="login.otp.verifyEmail" />
        </p>
        <div className="my-4 text-center">
          <Link
            // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
            className="underline hover:text-[var(--color-primary)]"
            data-qa-id="otp-back-to-login-with-password"
            href="#"
            onClick={() => {
              setLoginOtpState(LOGIN_OTP_STATE.LOGIN_REGISTER)
            }}
          >
            <FormattedMessage id="login.otp.backToLoginWithPassword" />
          </Link>
        </div>
        <FriendlyCaptchaChallenge
          doneCallback={captchaDoneCallback}
          startMode="auto"
          whenThisPropChangesResetCaptcha={otpSwr.error}
        />
      </form>
    </>
  )
}
